import { useParams, useNavigate, useLocation } from "react-router-dom";
import useJobDetails from "../../hooks/useJobDetails";
import { useEffect, useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { useAtom } from "jotai";
import { expandedJobDetailsAtomObject, originalDataAtomObject, isLoadingAtomBool } from "../../state";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { Link } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import SkeletonJobDetails from "./SkeletonJobDetails";
const JobDetailsPage = ({ slug }) => {
  const { id } = useParams();
  const location = useLocation();
  const jobDetails = useJobDetails();
  const [expandedJobDetails] = useAtom(expandedJobDetailsAtomObject);
  const [originalData] = useAtom(originalDataAtomObject);
  const navigate = useNavigate();
  const [isLoading] = useAtom(isLoadingAtomBool);
  // Find job from jobs array by id or slug



  let job = null;
  if (id) {
    job = originalData.jobs?.find(j => String(j.id) === String(id));
  } else if (slug) {
    job = originalData.jobs?.find(j => j.slug === slug);
  }
  // Fallback to router state or expandedJobDetails
  job = job || location.state?.job || expandedJobDetails;

  const [jobLoaded, setJobLoaded] = useState(false);


  // Use job data for schema
  const jobTitle = job?.title || "";
  const jobDescription = job?.description || "";
  const jobLocation = job?.location?.address || "";
  const jobDatePosted = job?.postDate || "";
  const jobValidThrough = job?.expiryDate || "";
  const jobSalaryCurrency = job?.salaryCurrency || "";
  const jobSalary = job?.salaryText || "";
  const jobHiringOrg =  job?.tenantName || "";
  const jobHiringURL =  window.location.origin;
  const jobHiringLogo =  "";
  const jobTypes = job?.jobTypes || "";
  const jobTypeText = Array.isArray(jobTypes) ? jobTypes[0] : jobTypes;

  const jobPostingSchema = {
    "@context": "https://schema.org/",
    "@type": "JobPosting",
    "title": jobTitle,
    "description": jobDescription,
    "datePosted": jobDatePosted,
    "validThrough": jobValidThrough,
    "employmentType": jobTypeText,
    "hiringOrganization": {
      "@type": "Organization",
      "name": jobHiringOrg,
      "sameAs": jobHiringURL,
    },
    "jobLocation": {
      "@type": "Place",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": jobLocation,
        "addressCountry": ""
      }
    },
    "baseSalary": {
      "@type": "MonetaryAmount",
      "currency": jobSalaryCurrency,
      "value": {
        "@type": "QuantitativeValue",
        "value": jobSalary,
        "unitText": ""
      }
    }
  };

let plugin_date = new Date();    
let plugin_version = `${plugin_date.getUTCFullYear()}${(plugin_date.getUTCMonth() + 1)}${plugin_date.getUTCDate()}${plugin_date.getUTCHours()}${plugin_date.getUTCMinutes()}`;

  const triggerRecruitmentCss = (css_elements) => {
    if (!css_elements || css_elements.length === 0) {
      return;
    }

    const loadCssSequentially = (index) => {
      if (index >= css_elements.length) {
        console.log("All CSS loaded.");
        return;
      }
      const css = css_elements[index];
      css.onload = () => {
        console.log(`Loaded CSS #${index}`);
        loadCssSequentially(index + 1);
      };
      console.log(`Appending CSS #${index}`);
      document.head.appendChild(css);
    };

    loadCssSequentially(0);
  };

  const triggerRecruitmentScripts = (script_elements) => {
    const loadScriptSequentially = (index) => {
      if (index >= script_elements.length) {
        console.log("All scripts loaded.");
        return;
      }
      const script = script_elements[index];
      script.onload = () => {
        console.log(`Loaded script #${index}`);
        loadScriptSequentially(index + 1);
      };
      console.log(`Appending script #${index}`);
      document.body.appendChild(script);
    };

    if (script_elements.length > 0) {
      loadScriptSequentially(0);
    }
  };


  const deploySmartApplicationScripts = () => {
    let script_elements = [];
    let scripts = [
      process.env.REACT_APP_CDN_ROOT_URL + 'smartapplication/js/main.js?v=' + plugin_version,
      process.env.REACT_APP_CDN_ROOT_URL + 'smartapplication/js/chunk.js?v=' + plugin_version
    ];

    scripts.forEach((current_script) => {
      // Remove 
      document.querySelectorAll(`script[src="${current_script}"]`).forEach(el => el.parentNode.removeChild(el));

      // Check
      if (!document.querySelector(`script[src="${current_script}"]`)) {
        var script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = current_script;
        script_elements.push(script);
      }
    });

    return script_elements;
  };


  const deployRecruitmentCss = () => {

    let css_elements = [];

    //Load the scripts on the page
    let css_list = [
      process.env.REACT_APP_CDN_ROOT_URL + 'common/css/all.min.css?v=' + plugin_version,
      process.env.REACT_APP_CDN_ROOT_URL + 'smartapplication/css/main.css?v=' + plugin_version
    ];

    css_list.forEach((current_css, i) => {
      let elements = document.getElementsByTagName("link");
      let found = false;

      for (let i = 0; i < elements.length; i++) {
        let current_element = elements[i];
        if (current_element.getAttribute("href") === current_css) {
          found = true;
          break;
        }
      }

      if (!found) {
        var css_link = document.createElement('link');
        css_link.rel = 'stylesheet';
        css_link.type = 'text/css';
        css_link.href = current_css;
        css_elements.push(css_link);
      }
    });

    return css_elements;
  };


useEffect(() => {
  // Check if job has external URL and redirect
  if (job?.url && job.url !== "" && job.url !== "#") {
    window.location.href = job.url;
    return;
  }

  // Only load job details if no external URL
  if (!isLoading && jobLoaded) return; 
  const loadJob = async () => {
    if (slug) {
      await jobDetails(null, { slug });
    } else {
      await jobDetails(null, { slug: null });
    }
    setJobLoaded(true);
  };
  loadJob();
}, [slug, job?.url]);

const hasSmartApplicationDiv = (() => {
  if (!expandedJobDetails?.html) return false;
  const parser = new DOMParser();
  const doc = parser.parseFromString(expandedJobDetails.html, "text/html");
  return !!doc.getElementById("vennture_dynamic_smartapplication");
})();

   useEffect(() => {
    if (
    !jobLoaded ||
    !expandedJobDetails ||
    !hasSmartApplicationDiv
  ) {
    return;
  }
      const customEvent = new CustomEvent('message', {
        detail: job.id > 0
          ? job.id.toString()
          : job.slug
      });
      window.dispatchEvent(customEvent);
   
  }, [hasSmartApplicationDiv]);


 useEffect(() => {
  if (
    !jobLoaded ||
    !expandedJobDetails ||
    !hasSmartApplicationDiv
  ) {
    return;
  }

  let attempts = 0;
  const maxAttempts = 50;
  const intervalId = setInterval(() => {
    const container = document.getElementById("vennture_dynamic_smartapplication");
    if (container) {
      clearInterval(intervalId);
      const css_elements = deployRecruitmentCss();
      const smart_app_scripts = deploySmartApplicationScripts();
      triggerRecruitmentCss(css_elements);
      triggerRecruitmentScripts([...smart_app_scripts]);
    } else if (++attempts > maxAttempts) {
      clearInterval(intervalId);
      console.error("vennture_dynamic_smartapplication not found in DOM after waiting.");
    }
  }, 100);

  return () => {
    clearInterval(intervalId);
  };
}, [jobLoaded, expandedJobDetails, hasSmartApplicationDiv]);

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>{jobTitle}</title>
          <meta name="description" content={jobDescription} />
          <script type="application/ld+json">
            {JSON.stringify(jobPostingSchema)}
          </script>
        </Helmet>
        <div className="padding-bottom vsm-job-details">
          <div className="bg">
            <div className="component-wrapper">
              <div className="vsm-body">
                <Container>
                  <Row>
                    <Col>
                     {isLoading ? <div style={{ margin: "1.875rem 0" }}><Skeleton width={150} /></div> :(
                      <Link
                        href="#"
                        className="back-to-link"
                        onClick={e => {
                          e.preventDefault();
                          // navigate("/?override=https%3A%2F%2Ftest-third-party-careers-site.devstaging.wearevennture.co.uk");
                          navigate(window.location.pathname.split("?")[0]);
                        }}
                      >
                        <span>Back to results</span>
                      </Link>
                     )}
                      
                    </Col>
                  </Row>
                  {isLoading  ? <SkeletonJobDetails/> : (
                    <div dangerouslySetInnerHTML={{ __html: expandedJobDetails.html || "" }} />
                  )}
                </Container>
              </div>
            </div>
          </div>
        </div>
      </HelmetProvider>
    </>
  );
};

export default JobDetailsPage;
